import toast, { Toast } from "react-hot-toast";
import { ErrorCode } from "api/errors/errors";
import ErrorSnack from "components/Error/ErrorSnack";
import { ReactComponent as CriticalError } from "icons/Error/CriticalError.svg";
import { ReactComponent as NetworkError } from "icons/Error/NetworkError.svg";
import { refreshAccessToken } from "utils/oauth";
import { handleLogout, isTokenExpired } from "utils/auth";
import { JWT_REFRESH_TOKEN_FIELD, JWT_TOKEN_FIELD } from "utils/constants";
import { jwtDecode } from "jwt-decode";

interface ErrorConfiguration {
  icon: React.FC;
  color: string;
  iconBackgroundColor: string;
  actionBackground: string;
  backgroundColor: string;
  borderTop: string;
  actionTextColor: string;
}

export const ERROR_CONFIG: { [key: string]: ErrorConfiguration } = {
  network: {
    icon: NetworkError,
    color: "#E9E4FF",
    iconBackgroundColor: "#2E2A40",
    actionBackground: "rgba(41, 38, 53, 0.6)",
    borderTop: "none",
    actionTextColor: "rgba(255, 255, 255, 0.66)",
    backgroundColor: "#1D1A2C",
  },
  critical: {
    icon: CriticalError,
    color: "#EB6868",
    iconBackgroundColor: "rgba(190, 33, 33, 0.16)",
    actionBackground: "#241a28",
    borderTop: "1px solid rgb(52, 40, 54)",
    actionTextColor: "white",
    backgroundColor: "#221725",
  },
};

const SIGN_IN = "Sign In";
const HOME = "Go to Homepage";
const REFRESH = "Refresh";

const toastConfig = {
  id: "error-toast",
  duration: 5000,
  style: {
    background: "transparent",
  },
};

const showToast = (
  t: Toast,
  message: string,
  subtitle: string,
  styleConfig,
  actionText: string
) => {
  const handleClick = () => {
    toast.dismiss(t.id);
    if (actionText === SIGN_IN) {
      handleLogout();
    } else if (actionText === REFRESH) {
      window.location.reload();
    }
  };

  return (
    <ErrorSnack
      title={message}
      subtitle={subtitle}
      icon={styleConfig.icon}
      iconColor={styleConfig.color}
      iconBackground={styleConfig.iconBackgroundColor}
      actionBackground={styleConfig.actionBackground}
      backgroundColor={styleConfig.backgroundColor}
      actionTextColor={styleConfig.actionTextColor}
      borderTop={styleConfig.borderTop}
      actionText={actionText}
      actionUrl={
        actionText === SIGN_IN
          ? "/login"
          : actionText === HOME
          ? "/"
          : undefined
      }
      onActionClick={handleClick}
    />
  );
};

export const showError = (
  message: string,
  subtitle: string,
  actionText: string,
  styleConfig: ErrorConfiguration = ERROR_CONFIG.critical
) => {
  toast(
    (t) => showToast(t, message, subtitle, styleConfig, actionText),
    toastConfig
  );
};

const redirectToErrorPage = () => {
  // Clears local storage
  handleLogout();
  window.location.href = "/something-went-wrong";
};

const redirectToLoginPage = () => {
  // Clears local storage
  handleLogout();
  window.location.href = "/login";
};

export const handleErrorByCode = (code: ErrorCode, message: string) => {
  switch (code) {
    case ErrorCode.PERMISSION_DENIED:
      showError(
        "You don't have access to this resource.",
        "",
        HOME,
        ERROR_CONFIG.network
      );
      break;
    case ErrorCode.UNAUTHENTICATED: {
      const jwtToken = localStorage.getItem(JWT_TOKEN_FIELD);
      const refreshToken = localStorage.getItem(JWT_REFRESH_TOKEN_FIELD);

      // Check if token is valid using jwtDecode
      try {
        if (jwtToken) {
          jwtDecode(jwtToken);
        } else {
          redirectToLoginPage();
          return;
        }
      } catch (error) {
        console.error("Invalid JWT token:", error);
        redirectToLoginPage();
        return;
      }

      // If we can, refresh the access token
      if (isTokenExpired(jwtToken)) {
        if (refreshToken) {
          refreshAccessToken().then((success) => {
            if (!success) {
              showError(
                "Your session has expired",
                "Please sign back in.",
                SIGN_IN
              );
            }
          });
        } else {
          redirectToErrorPage();
        }
      } else {
        showError(
          "You don't have access to this resource.",
          "",
          HOME,
          ERROR_CONFIG.network
        );
      }
      break;
    }

    case ErrorCode.UNAVAILABLE:
      showError(
        "A network error occurred.",
        "Please refresh the page",
        REFRESH,
        ERROR_CONFIG.network
      );
      break;

    // Silently handle internal errors
    // case ErrorCode.INTERNAL:
    //   showError("An internal error occurred", "Please refresh the page", refreshPage, "Refresh", ERROR_CONFIG.network);
    //   break;

    case ErrorCode.RESOURCE_EXHAUSTED:
      showError(
        "Slow down Tiger!",
        "This API is rate limited. Please try again after a minute.",
        ""
      );
      break;
    default:
      console.error(`Unhandled error: ${code} ${message}`);
      break;
  }
};
